import { METERS_PER_UNIT } from 'ol/proj';

/**
 * @private
 * Function to memoize style conversion functions that convert sld symbolizers to OpenLayers style instances.
 * The memoized version of the style converter returns the same OL style instance if the symbolizer is the same object.
 * Uses a WeakMap internally.
 * Note: This only works for constant symbolizers.
 * @param {Function} styleFunction Function that accepts a single symbolizer object and returns the corresponding OpenLayers style object.
 * @returns {Function} The memoized function of the style conversion function.
 */
export function memoizeStyleFunction(styleFunction) {
  const styleCache = new WeakMap();

  return symbolizer => {
    let olStyle = styleCache.get(symbolizer);

    // Create a new style if no style has been created yet, or when symbolizer has been invalidated.
    if (!olStyle || symbolizer.__invalidated) {
      olStyle = styleFunction(symbolizer);
      // Clear invalidated flag after creating a new style instance.
      symbolizer.__invalidated = false;
      styleCache.set(symbolizer, olStyle);
    }

    return olStyle;
  };
}

export function convertSize(numericSize, mapResolution, mapUom, targetUom) {
  const size = Number(numericSize);
  const resolution = Number(mapResolution);

  if (!Number.isFinite(size)) {
    return numericSize;
  }

  // If we cannot resolve scale, return the input value unchanged.
  if (!Number.isFinite(resolution) || resolution === 0) {
    return size;
  }

  const normalizedTargetUom = normalizeUom(targetUom);
  if (!normalizedTargetUom || normalizedTargetUom === 'px') {
    return size;
  }

  const targetMetersPerUnit = getMetersPerUnit(normalizedTargetUom);
  const mapMetersPerUnit = getMetersPerUnit(mapUom);
  if (
    !Number.isFinite(targetMetersPerUnit) ||
    targetMetersPerUnit <= 0 ||
    !Number.isFinite(mapMetersPerUnit) ||
    mapMetersPerUnit <= 0
  ) {
    return size;
  }

  const sizeInMapUnits = (size * targetMetersPerUnit) / mapMetersPerUnit;
  return sizeInMapUnits / resolution;
}

function getMetersPerUnit(uom) {
  const normalizedUom = normalizeUom(uom);
  if (!normalizedUom) {
    return null;
  }

  if (normalizedUom === 'px') {
    // OGC standardized rendering pixel size: 0.28 mm.
    return 0.00028;
  }

  if (normalizedUom === 'mm') {
    return 0.001;
  }

  if (normalizedUom === 'cm') {
    return 0.01;
  }

  if (normalizedUom === 'km') {
    return 1000;
  }

  if (normalizedUom === 'in') {
    return 0.0254;
  }

  return METERS_PER_UNIT[normalizedUom] ?? null;
}

function normalizeUom(uom) {
  if (uom == null) {
    return null;
  }

  const value = String(uom).trim().toLowerCase();
  switch (value) {
    case 'pixel':
    case 'pixels':
    case 'px':
    case 'http://www.opengeospatial.org/se/units/pixel':
    case 'urn:ogc:def:uom:ogc:1.0:pixel':
      return 'px';
    case 'metre':
    case 'meter':
    case 'meters':
    case 'metres':
    case 'm':
    case 'http://www.opengeospatial.org/se/units/metre':
    case 'urn:ogc:def:uom:ogc:1.0:metre':
      return 'm';
    case 'foot':
    case 'feet':
    case 'ft':
    case 'http://www.opengeospatial.org/se/units/foot':
    case 'urn:ogc:def:uom:ogc:1.0:foot':
      return 'ft';
    case 'us-ft':
    case 'us_survey_foot':
    case 'ussurveyfoot':
      return 'us-ft';
    case 'degree':
    case 'degrees':
    case 'deg':
      return 'degrees';
    case 'radian':
    case 'radians':
    case 'rad':
      return 'radians';
    case 'kilometer':
    case 'kilometers':
    case 'kilometre':
    case 'kilometres':
    case 'km':
      return 'km';
    case 'centimeter':
    case 'centimeters':
    case 'centimetre':
    case 'centimetres':
    case 'cm':
      return 'cm';
    case 'millimeter':
    case 'millimeters':
    case 'millimetre':
    case 'millimetres':
    case 'mm':
      return 'mm';
    case 'inch':
    case 'inches':
    case 'in':
      return 'in';
    default:
      return value;
  }

}

/**
 * @private
 * Clone an OpenLayers style value returned by a style converter.
 * Accepts a single style, an array of styles, or any other value.
 * Returns the same value when clone is not available.
 * @param {*} styleValue Style or array of styles.
 * @returns {*} Cloned style value.
 */
export function cloneStyleValue(styleValue) {
  if (Array.isArray(styleValue)) {
    return styleValue.map(item => cloneStyleValue(item));
  }
  if (styleValue && typeof styleValue.clone === 'function') {
    return styleValue.clone();
  }
  return styleValue;
}

export function memoizeSymbolizerInfoFunction() {
    const symbolizerInfoCache = new WeakMap();
    return symbolizer => {

        function hasNoProperties(obj) {
            if (typeof (obj) == "object" && obj !== null) {
                var keys = Object.keys(obj);
                for (var i = 0; i < keys.length; i++) {
                    var ok = hasNoProperties(obj[keys[i]]);
                    if (!ok) {
                        return false;
                    }
                }
            }
            else if (obj === "expression") {
                return false;
            }
            return true;
        }


        let symbolizerInfo = symbolizerInfoCache.get(symbolizer);

        // Create a new style if no style has been created yet, or when symbolizer has been invalidated.
        if (!symbolizerInfo) {

            symbolizerInfo = {};
            symbolizerInfo.hasPropeties = !hasNoProperties(symbolizer);
            symbolizerInfoCache.set(symbolizer, symbolizerInfo);
        }

        return symbolizerInfo;
    };
}

/**
 * @private
 * Convert a hex color (like #AABBCC) to an rgba-string.
 * @param  {string} hex   eg #AA00FF
 * @param  {Number} alpha eg 0.5
 * @return {string}       rgba(0,0,0,0)
 */
export function hexToRGB(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (typeof alpha !== 'undefined' && alpha !== null) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * @private
 * Calculate the center-to-center distance for graphics placed along a line within a GraphicSymbolizer.
 * @param {object} lineSymbolizer SLD line symbolizer object.
 * @param {number} graphicWidth Width of the symbolizer graphic in pixels. This size may be dependent on feature properties,
 * so it has to be supplied separately from the line symbolizer object.
 * @returns {number} Center-to-center distance for graphics along a line.
 */
export function calculateGraphicSpacing(lineSymbolizer, graphicWidth) {
    const { graphicstroke, styling } = lineSymbolizer.stroke;

    if ('gap' in graphicstroke) {
        // Note: gap should be a numeric property after parsing (check reader.test).
        const numericGap = Number(graphicstroke.gap);
        if (!Number.isNaN(numericGap)) {
            return numericGap + graphicWidth;
        }
    }

    // If gap is not given, use strokeDasharray to space graphics.
    // First digit represents size of graphic, second the relative space, e.g.
    // size = 20, dash = [2 6] -> 2 ~ 20 then 6 ~ 60, total segment length should be 20 + 60 = 80
    let multiplier = 1; // default, i.e. a segment is the size of the graphic (without stroke/outline).
    if (styling && styling.strokeDasharray) {
    const dash = styling.strokeDasharray.split(' ');
        if (dash.length >= 2 && dash[0] !== 0) {
            multiplier = dash[1] / dash[0] + 1;
        }
    }
    return multiplier * graphicWidth;
}
